import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import Header from "../../Common/Layout/Header";
import Modal from "react-bootstrap/Modal";
import DeleteAlert from "../../Common/Layout/DeleteAlert";
import { format } from "date-fns";
import currencyFlags from "../../Data/CurrencyFlags.json";
import AccountTabs from "../Accounts/AccountTabs";

const TaxCollection = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // State for tax collections list
  const [taxCollections, setTaxCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  // Countries state
  const [countries, setCountries] = useState([]);

  // Modal states
  const [editShow, setEditShow] = useState(false);
  const [editTaxCollection, setEditTaxCollection] = useState({
    taxCollectionId: null,
    companyId: null,
    taxName: "",
    taxType: "",
    countryCode: "",
    stateCode: "",
    applicableOn: "",
    transactionType: "",
    rate: 0,
    thresholdAmount: null,
    currencyCode: "",
    isPercentage: true,
    isActive: true,
    effectiveFrom: new Date().toISOString().slice(0, 16),
    effectiveTo: null,
    notes: "",
  });
  const [editBtnLoading, setEditBtnLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Options for dropdowns
  const taxTypes = ["COLLECTION", "WITHHOLDING"];
  const applicableOnOptions = ["Sale", "Payment"];
  const transactionTypeOptions = ["Invoice", "Receipt"];

  // Load countries from currencyFlags
  useEffect(() => {
    if (currencyFlags && Array.isArray(currencyFlags)) {
      const sortedCountries = [...currencyFlags].sort((a, b) =>
        a.country.localeCompare(b.country)
      );
      setCountries(sortedCountries);
    }
  }, []);

  // Get all tax collections
  const getTaxCollections = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const response = await apiService.getAll(config.GETALLTAXCOLLECTIONS);
      const data = response.data || response || [];
      setTaxCollections(data);
    } catch (error) {
      console.error("Error fetching tax collections:", error);
      setTaxCollections([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && user) {
      getTaxCollections();
    }
  }, [user, authLoading, getTaxCollections]);

  // Handle Add Tax Collection
  const handleAddTaxCollection = () => {
    setEditTaxCollection({
      taxCollectionId: null,
      companyId: null,
      taxName: "",
      taxType: "",
      countryCode: "",
      stateCode: "",
      applicableOn: "",
      transactionType: "",
      rate: 0,
      thresholdAmount: null,
      currencyCode: "",
      isPercentage: true,
      isActive: true,
      effectiveFrom: new Date().toISOString().slice(0, 16),
      effectiveTo: null,
      notes: "",
    });
    setFormErrors({});
    setEditShow(true);
  };

  // Handle Edit Tax Collection
  const handleEditClick = (taxCollection) => {
    const editData = {
      ...taxCollection,
      taxCollectionId: taxCollection.taxCollectionId,
      effectiveFrom: taxCollection.effectiveFrom
        ? new Date(taxCollection.effectiveFrom).toISOString().slice(0, 16)
        : new Date().toISOString().slice(0, 16),
      effectiveTo: taxCollection.effectiveTo
        ? new Date(taxCollection.effectiveTo).toISOString().slice(0, 16)
        : null,
      thresholdAmount: taxCollection.thresholdAmount || null,
    };
    setEditTaxCollection(editData);
    setFormErrors({});
    setEditShow(true);
  };

  // Close edit modal
  const handleEditClose = () => {
    setEditShow(false);
    setEditTaxCollection({
      taxCollectionId: null,
      companyId: null,
      taxName: "",
      taxType: "",
      countryCode: "",
      stateCode: "",
      applicableOn: "",
      transactionType: "",
      rate: 0,
      thresholdAmount: null,
      currencyCode: "",
      isPercentage: true,
      isActive: true,
      effectiveFrom: "",
      effectiveTo: null,
      notes: "",
    });
    setFormErrors({});
    setEditBtnLoading(false);
  };

  // Handle input change
  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle number inputs
    if (type === "number") {
      setEditTaxCollection({
        ...editTaxCollection,
        [name]: value === "" ? null : parseFloat(value),
      });
    } else if (type === "checkbox") {
      setEditTaxCollection({
        ...editTaxCollection,
        [name]: checked,
      });
    } else {
      setEditTaxCollection({
        ...editTaxCollection,
        [name]: value,
      });
    }

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: null });
    }
  };

  // Handle country change - auto-populate currency (like CompanyClients)
  const handleCountryChange = (countryCode) => {
    const selectedCountry = countries.find(
      (country) => country.countryCode === countryCode
    );
    setEditTaxCollection({
      ...editTaxCollection,
      countryCode: countryCode,
      currencyCode: selectedCountry?.code || "",
    });
  };

  // Handle select change for dropdowns
  const handleSelectChange = (e) => {
    const { name, value } = e.target;

    // Convert string to boolean for isActive and isPercentage
    if (name === "isActive" || name === "isPercentage") {
      setEditTaxCollection({
        ...editTaxCollection,
        [name]: value === "true",
      });
    } else {
      setEditTaxCollection({
        ...editTaxCollection,
        [name]: value,
      });
    }

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: null });
    }
  };

  // Handle currency change
  const handleCurrencyChange = (currencyCode) => {
    setEditTaxCollection({
      ...editTaxCollection,
      currencyCode: currencyCode,
    });
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!editTaxCollection.taxName || editTaxCollection.taxName.trim() === "") {
      errors.taxName = "Tax name is required";
    }

    if (!editTaxCollection.taxType) {
      errors.taxType = "Tax type is required";
    }

    if (!editTaxCollection.countryCode) {
      errors.countryCode = "Country is required";
    }

    if (!editTaxCollection.applicableOn) {
      errors.applicableOn = "Applicable on is required";
    }

    if (!editTaxCollection.transactionType) {
      errors.transactionType = "Transaction type is required";
    }

    if (editTaxCollection.rate === undefined || editTaxCollection.rate === null || editTaxCollection.rate < 0) {
      errors.rate = "Rate must be a valid positive number";
    }

    if (!editTaxCollection.effectiveFrom) {
      errors.effectiveFrom = "Effective from date is required";
    }

    if (editTaxCollection.effectiveFrom && editTaxCollection.effectiveTo) {
      const fromDate = new Date(editTaxCollection.effectiveFrom);
      const toDate = new Date(editTaxCollection.effectiveTo);
      if (toDate <= fromDate) {
        errors.effectiveTo = "Effective to date must be after effective from date";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle Save Tax Collection
  const handleSaveTaxCollection = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setEditBtnLoading(true);
    try {
      const taxCollectionData = {
        companyId: editTaxCollection.companyId || null,
        taxName: editTaxCollection.taxName.trim(),
        taxType: editTaxCollection.taxType,
        countryCode: editTaxCollection.countryCode, // Only country code is sent
        stateCode: editTaxCollection.stateCode || "",
        applicableOn: editTaxCollection.applicableOn,
        transactionType: editTaxCollection.transactionType,
        rate: parseFloat(editTaxCollection.rate),
        thresholdAmount: editTaxCollection.thresholdAmount ? parseFloat(editTaxCollection.thresholdAmount) : null,
        currencyCode: editTaxCollection.currencyCode || "", // Currency code from selected country
        isPercentage: editTaxCollection.isPercentage,
        isActive: editTaxCollection.isActive,
        effectiveFrom: new Date(editTaxCollection.effectiveFrom).toISOString(),
        effectiveTo: editTaxCollection.effectiveTo ? new Date(editTaxCollection.effectiveTo).toISOString() : null,
        notes: editTaxCollection.notes || "",
      };

      // Only add taxCollectionId for update operations
      if (editTaxCollection.taxCollectionId) {
        taxCollectionData.taxCollectionId = editTaxCollection.taxCollectionId;
      }

      let response;
      if (editTaxCollection.taxCollectionId) {
        // Update existing tax collection
        response = await apiService.update(
          config.UPDATETAXCOLLECTION,
          taxCollectionData
        );
      } else {
        // Create new tax collection
        response = await apiService.create(
          config.CREATETAXCOLLECTION,
          taxCollectionData
        );
      }

      if (response && (response.success || response === true)) {
        await getTaxCollections();
        handleEditClose();
      } else {
        console.error("Failed to save tax collection");
        setFormErrors({ submit: "Failed to save tax collection. Please try again." });
      }
    } catch (error) {
      console.error("Error saving tax collection:", error);
      setFormErrors({
        submit: error.response?.data?.title ||
          error.response?.data?.message ||
          "An error occurred while saving"
      });
    } finally {
      setEditBtnLoading(false);
    }
  };

  // Delete functionality
  const handleOpenDeleteModal = (taxCollectionId) => {
    setItemToDelete(taxCollectionId);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleDelete = async () => {
    if (itemToDelete) {
      try {
        await apiService.remove(
          config.DELETETAXCOLLECTION,
          itemToDelete
        );
        await getTaxCollections();
      } catch (error) {
        console.error("Error deleting tax collection:", error);
      }
      handleCloseDeleteModal();
    }
  };

  // Get country name from code
  const getCountryName = (code) => {
    const country = countries.find((c) => c.countryCode === code);
    return country ? country.country : code;
  };

  // Get country flag from code
  const getCountryFlag = (code) => {
    const country = countries.find((c) => c.countryCode === code);
    return country ? country.flag : null;
  };

  // Format date as "12-Aug-2026"
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "dd-MMM-yyyy");
    } catch {
      return "-";
    }
  };

  // Format currency
  const formatCurrency = (amount, currencyCode) => {
    if (!amount && amount !== 0) return "-";
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode || 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return `${currencyCode || ''} ${amount}`;
    }
  };

  if (authLoading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
    return null;
  }

  return (
    <>
      <Header />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="card">

              <div className="p-2">
                <AccountTabs pageName="TAX COLLECTION" />
              </div>
              <div className="card-body">
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="card-title mb-0">
                        <i className="ri-bank-card-line me-2"></i>
                        Tax Collection Management
                      </h5>
                      <button className="btn btn-primary" onClick={handleAddTaxCollection}>
                        <i className="ri-add-line me-2"></i>
                        Add Tax Collection
                      </button>
                    </div>
                  </div>
                </div>

                {/* Table Section */}
                <div className="table-responsive table-card">
                  <table className="table align-middle table-hover">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "50px" }}>#</th>
                        <th>Tax Name</th>
                        <th>Type</th>
                        <th>Country</th>
                        <th>Applicable On</th>
                        <th>Transaction Type</th>
                        <th>Rate</th>
                        <th>Threshold</th>
                        <th>Effective From</th>
                        <th>Status</th>
                        <th style={{ width: "120px" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="11" className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2">Loading tax collections...</p>
                          </td>
                        </tr>
                      ) : taxCollections.length > 0 ? (
                        taxCollections.map((taxCollection, index) => (
                          <tr key={taxCollection.taxCollectionId}>
                            <td>{index + 1}</td>
                            <td>
                              <strong>{taxCollection.taxName}</strong>
                              {taxCollection.notes && (
                                <div>
                                  <small className="text-muted">{taxCollection.notes}</small>
                                </div>
                              )}
                            </td>
                            <td>
                              <span className={`badge ${taxCollection.taxType === 'COLLECTION' ? 'bg-primary' : 'bg-warning'}`}>
                                {taxCollection.taxType}
                              </span>
                            </td>
                            <td>
                              {getCountryFlag(taxCollection.countryCode) && (
                                <img
                                  src={getCountryFlag(taxCollection.countryCode)}
                                  alt={taxCollection.countryCode}
                                  style={{
                                    width: "20px",
                                    height: "15px",
                                    marginRight: "6px",
                                    verticalAlign: "middle",
                                  }}
                                />
                              )}
                              {getCountryName(taxCollection.countryCode)}
                              {taxCollection.stateCode && (
                                <small className="text-muted d-block">State: {taxCollection.stateCode}</small>
                              )}
                              {taxCollection.currencyCode && (
                                <small className="text-muted d-block">Currency: {taxCollection.currencyCode}</small>
                              )}
                            </td>
                            <td>
                              <span className="badge bg-info">{taxCollection.applicableOn}</span>
                            </td>
                            <td>
                              <span className="badge bg-secondary">{taxCollection.transactionType}</span>
                            </td>
                            <td>
                              <strong>
                                {taxCollection.isPercentage ? `${taxCollection.rate}%` : taxCollection.rate}
                              </strong>
                            </td>
                            <td>
                              {taxCollection.thresholdAmount ? (
                                <span>
                                  {formatCurrency(taxCollection.thresholdAmount, taxCollection.currencyCode)}
                                </span>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>{formatDate(taxCollection.effectiveFrom)}</td>
                            <td>
                              <span
                                className={`badge ${taxCollection.isActive ? "bg-success" : "bg-danger"}`}
                              >
                                {taxCollection.isActive ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td>
                              <ul className="list-inline hstack gap-2 mb-0">
                                <li
                                  className="list-inline-item"
                                  data-bs-toggle="tooltip"
                                  data-bs-trigger="hover"
                                  data-bs-placement="top"
                                  title="Edit"
                                >
                                  <Link
                                    className="edit-item-btn"
                                    onClick={() => handleEditClick(taxCollection)}
                                  >
                                    <i className="ri-pencil-fill align-bottom text-muted" />
                                  </Link>
                                </li>
                                <li
                                  className="list-inline-item"
                                  data-bs-toggle="tooltip"
                                  data-bs-trigger="hover"
                                  data-bs-placement="top"
                                  title="Delete"
                                >
                                  <Link
                                    className="remove-item-btn"
                                    onClick={() =>
                                      handleOpenDeleteModal(taxCollection.taxCollectionId)
                                    }
                                  >
                                    <i className="ri-delete-bin-fill align-bottom text-muted" />
                                  </Link>
                                </li>
                              </ul>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="11" className="text-center py-4">
                            <i className="ri-inbox-line fs-1 text-muted d-block mb-2"></i>
                            No tax collections found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Total count */}
                <div className="row mt-3">
                  <div className="col-12">
                    <span className="text-muted">
                      Total Tax Collections: {taxCollections.length}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add/Edit Tax Collection Modal */}
        <Modal
          show={editShow}
          onHide={handleEditClose}
          size="lg"
          centered
          scrollable
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {editTaxCollection.taxCollectionId ? (
                <>
                  <i className="ri-edit-line me-2 text-primary"></i>
                  Edit Tax Collection - {editTaxCollection.taxName}
                </>
              ) : (
                <>
                  <i className="ri-add-line me-2 text-primary"></i>
                  Add New Tax Collection
                </>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
            <form onSubmit={handleSaveTaxCollection}>
              <div className="container-fluid">
                {/* Error message */}
                {formErrors.submit && (
                  <div className="alert alert-danger alert-dismissible fade show mb-3">
                    <i className="ri-error-warning-line me-2"></i>
                    {formErrors.submit}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setFormErrors({ ...formErrors, submit: null })}
                    ></button>
                  </div>
                )}

                <div className="row">
                  {/* Tax Name */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">
                      Tax Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.taxName ? "is-invalid" : ""}`}
                      name="taxName"
                      value={editTaxCollection.taxName}
                      onChange={handleEditInputChange}
                      placeholder="e.g., TCS 5%, Withholding Tax"
                      maxLength={100}
                    />
                    {formErrors.taxName && (
                      <div className="invalid-feedback">{formErrors.taxName}</div>
                    )}
                  </div>

                  {/* Tax Type */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">
                      Tax Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-select ${formErrors.taxType ? "is-invalid" : ""}`}
                      name="taxType"
                      value={editTaxCollection.taxType}
                      onChange={handleSelectChange}
                    >
                      <option value="">Select Tax Type</option>
                      {taxTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                    {formErrors.taxType && (
                      <div className="invalid-feedback">{formErrors.taxType}</div>
                    )}
                  </div>

                  {/* Country and Currency - Side by Side */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">
                      Country <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-select ${formErrors.countryCode ? "is-invalid" : ""}`}
                      name="countryCode"
                      value={editTaxCollection.countryCode}
                      onChange={(e) => handleCountryChange(e.target.value)}
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option key={country.countryCode} value={country.countryCode}>
                          {country.flag && (
                            <img
                              src={country.flag}
                              alt={country.country}
                              style={{ width: "20px", height: "15px", marginRight: "8px" }}
                            />
                          )}
                          {country.country} - {country.countryCode}
                        </option>
                      ))}
                    </select>
                    {formErrors.countryCode && (
                      <div className="invalid-feedback">{formErrors.countryCode}</div>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">
                      Currency <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-select ${formErrors.currencyCode ? "is-invalid" : ""}`}
                      name="currencyCode"
                      value={editTaxCollection.currencyCode}
                      onChange={(e) => handleCurrencyChange(e.target.value)}
                    >
                      <option value="">Select Currency</option>
                      {countries.map((country) => (
                        <option key={country.countryCode} value={country.code}>
                          {country.flag && (
                            <img
                              src={country.flag}
                              alt={country.country}
                              style={{ width: "20px", height: "15px", marginRight: "8px" }}
                            />
                          )}
                          {country.country} - {country.code}
                          {country.currency && ` (${country.currency})`}
                        </option>
                      ))}
                    </select>
                    <small className="text-muted d-block">
                      Automatically populated from selected country
                    </small>
                    {formErrors.currencyCode && (
                      <div className="invalid-feedback">{formErrors.currencyCode}</div>
                    )}
                  </div>

                  {/* State Code */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold">State Code</label>
                    <input
                      type="text"
                      className="form-control"
                      name="stateCode"
                      value={editTaxCollection.stateCode || ""}
                      onChange={handleEditInputChange}
                      placeholder="e.g., TS, CA, NY"
                      maxLength={10}
                    />
                    <small className="text-muted">
                      Optional - Leave empty for country-wide tax
                    </small>
                  </div>

                  {/* Applicable On */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold">
                      Applicable On <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-select ${formErrors.applicableOn ? "is-invalid" : ""}`}
                      name="applicableOn"
                      value={editTaxCollection.applicableOn}
                      onChange={handleSelectChange}
                    >
                      <option value="">Select</option>
                      {applicableOnOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    {formErrors.applicableOn && (
                      <div className="invalid-feedback">{formErrors.applicableOn}</div>
                    )}
                  </div>

                  {/* Transaction Type */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold">
                      Transaction Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-select ${formErrors.transactionType ? "is-invalid" : ""}`}
                      name="transactionType"
                      value={editTaxCollection.transactionType}
                      onChange={handleSelectChange}
                    >
                      <option value="">Select</option>
                      {transactionTypeOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    {formErrors.transactionType && (
                      <div className="invalid-feedback">{formErrors.transactionType}</div>
                    )}
                  </div>

                  {/* Rate */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold">
                      Rate <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className={`form-control ${formErrors.rate ? "is-invalid" : ""}`}
                      name="rate"
                      value={editTaxCollection.rate}
                      onChange={handleEditInputChange}
                      placeholder="e.g., 5"
                      min="0"
                      step="0.001"
                    />
                    {formErrors.rate && (
                      <div className="invalid-feedback">{formErrors.rate}</div>
                    )}
                  </div>

                  {/* Is Percentage - Dropdown */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold">
                      Is Percentage <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-select ${formErrors.isPercentage ? "is-invalid" : ""}`}
                      name="isPercentage"
                      value={editTaxCollection.isPercentage.toString()}
                      onChange={handleSelectChange}
                    >
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                    {formErrors.isPercentage && (
                      <div className="invalid-feedback">{formErrors.isPercentage}</div>
                    )}
                  </div>

                  {/* Threshold Amount */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold">Threshold Amount</label>
                    <input
                      type="number"
                      className="form-control"
                      name="thresholdAmount"
                      value={editTaxCollection.thresholdAmount || ""}
                      onChange={handleEditInputChange}
                      placeholder="e.g., 700000"
                      min="0"
                      step="0.01"
                    />
                    <small className="text-muted">Optional - Minimum amount for tax applicability</small>
                  </div>

                  {/* Status - Dropdown */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold">
                      Status <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-select ${formErrors.isActive ? "is-invalid" : ""}`}
                      name="isActive"
                      value={editTaxCollection.isActive.toString()}
                      onChange={handleSelectChange}
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                    {formErrors.isActive && (
                      <div className="invalid-feedback">{formErrors.isActive}</div>
                    )}
                  </div>

                  {/* Effective From */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold">
                      Effective From <span className="text-danger">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      className={`form-control ${formErrors.effectiveFrom ? "is-invalid" : ""}`}
                      name="effectiveFrom"
                      value={editTaxCollection.effectiveFrom}
                      onChange={handleEditInputChange}
                    />
                    {formErrors.effectiveFrom && (
                      <div className="invalid-feedback">{formErrors.effectiveFrom}</div>
                    )}
                  </div>

                  {/* Effective To */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold">Effective To</label>
                    <input
                      type="datetime-local"
                      className={`form-control ${formErrors.effectiveTo ? "is-invalid" : ""}`}
                      name="effectiveTo"
                      value={editTaxCollection.effectiveTo || ""}
                      onChange={handleEditInputChange}
                    />
                    <small className="text-muted">Optional - Leave empty for indefinite</small>
                    {formErrors.effectiveTo && (
                      <div className="invalid-feedback">{formErrors.effectiveTo}</div>
                    )}
                  </div>

                  {/* Notes */}
                  <div className="col-12 mb-3">
                    <label className="form-label fw-bold">Notes</label>
                    <textarea
                      className="form-control"
                      name="notes"
                      value={editTaxCollection.notes || ""}
                      onChange={handleEditInputChange}
                      placeholder="Additional notes about this tax collection"
                      rows={2}
                      maxLength={500}
                    />
                  </div>
                </div>

                {/* Form Actions */}
                <div className="row mt-3">
                  <div className="col-12">
                    <hr />
                    <div className="d-flex justify-content-end gap-2">
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={handleEditClose}
                        disabled={editBtnLoading}
                      >
                        <i className="ri-close-line me-1"></i>
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={editBtnLoading}
                      >
                        {editBtnLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            {editTaxCollection.taxCollectionId ? "Updating..." : "Creating..."}
                          </>
                        ) : (
                          <>
                            <i className={`ri-${editTaxCollection.taxCollectionId ? "save" : "add"}-line me-1`}></i>
                            {editTaxCollection.taxCollectionId ? "Update Tax Collection" : "Create Tax Collection"}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </Modal.Body>
        </Modal>

        {/* Delete Confirmation Modal */}
        <DeleteAlert
          show={showDeleteModal}
          handleClose={handleCloseDeleteModal}
          handleDelete={handleDelete}
          title="Delete Tax Collection"
          message="Are you sure you want to delete this tax collection? This action cannot be undone."
        />
      </div>
    </>
  );
};

export default TaxCollection;